import React from 'react';
import { Check, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Attachment style options
export const ATTACHMENT_OPTIONS = [
  {
    id: 'anxious_attachment',
    label: 'I seek deep reassurance',
    description: 'You love hard and fear being abandoned. You often wonder if you are too much or not enough.',
    color: '#A9756B'
  },
  {
    id: 'avoidant_attachment',
    label: 'I pull away when it gets too close',
    description: 'You value independence and often shut down or withdraw when relationships feel overwhelming.',
    color: '#C9A84C'
  },
  {
    id: 'disorganized_attachment',
    label: 'I feel confused in relationships',
    description: 'You want closeness but it also frightens you. You feel caught between pushing people away and pulling them close.',
    color: '#9f8a7d'
  },
  {
    id: 'secure_attachment',
    label: 'I feel mostly secure',
    description: 'You communicate your needs well and feel relatively comfortable with closeness and independence.',
    color: '#8b9a8b'
  }
];

// Healing focus options
export const HEALING_FOCUS_OPTIONS = [
  'Healing from a breakup or loss',
  'Co-parenting after heartbreak',
  'Breaking codependent patterns',
  'Rebuilding self-worth and identity',
  'Processing childhood wounds',
  'Managing anxiety and overwhelm',
  'Setting boundaries without guilt',
  'Learning to trust myself again'
];

// Nervous system response options
export const NERVOUS_SYSTEM_OPTIONS = [
  {
    id: 'fight_response',
    label: 'I fight',
    description: 'You get angry, reactive, or confrontational when stressed.',
    color: '#c94a4a'
  },
  {
    id: 'flight_response',
    label: 'I flee',
    description: 'You get busy, escape, or avoid the situation when stressed.',
    color: '#C9A84C'
  },
  {
    id: 'freeze_response',
    label: 'I freeze',
    description: 'You shut down, go numb, or can\'t think clearly when stressed.',
    color: '#6b7b8b'
  },
  {
    id: 'fawn_response',
    label: 'I fawn',
    description: 'You people-please, over-explain, or lose yourself trying to keep the peace.',
    color: '#A9756B'
  }
];

export const StepIndicator = ({ currentStep, totalSteps = 5 }) => (
  <div className="flex justify-center gap-2 mb-8">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div
        key={`step-${i + 1}`}
        className="h-1.5 rounded-full transition-all duration-300"
        style={{
          width: currentStep === i + 1 ? '2rem' : '0.5rem',
          backgroundColor: currentStep >= i + 1 ? '#C9A84C' : '#D5CFC6'
        }}
      />
    ))}
  </div>
);

export const OnboardingHeader = ({ currentStep, onBack }) => (
  <div className="flex items-center justify-between mb-6">
    {currentStep > 1 ? (
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-[#4A4A5A] hover:text-[#1A1A2E] transition-colors"
        data-testid="onboarding-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </button>
    ) : (
      <div />
    )}
    <span className="text-[#8A8798] text-sm">Step {currentStep} of 5</span>
  </div>
);

export const OptionCard = ({ selected, onClick, label, description, color, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
      selected ? 'scale-[1.02]' : 'hover:scale-[1.01]'
    }`}
    style={{
      backgroundColor: selected ? `${color}15` : '#F7F1EE',
      border: selected ? `2px solid ${color}` : '2px solid transparent'
    }}
  >
    <div className="flex items-center gap-3">
      <div 
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
          selected ? 'bg-[#EAE4DC]' : ''
        }`}
        style={{ 
          border: selected ? `2px solid ${color}` : '2px solid #A3A0AE',
          backgroundColor: selected ? color : 'transparent'
        }}
      >
        {selected && <Check className="w-3 h-3 text-[#1A1A2E]" />}
      </div>
      <div>
        <span className="text-[#1A1A2E] font-medium">{label}</span>
        {description && (
          <p className="text-[#6B6878] text-sm mt-1">{description}</p>
        )}
      </div>
    </div>
  </button>
);

export const MultiSelectOption = ({ selected, onClick, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full p-3 rounded-xl text-left transition-all ${
      selected ? 'bg-white/15' : 'bg-[#F7F1EE] hover:bg-[#F0EBE4]'
    }`}
    style={{
      border: selected ? '1px solid rgba(201, 168, 76, 0.5)' : '1px solid transparent'
    }}
  >
    <div className="flex items-center gap-3">
      <div 
        className={`w-5 h-5 rounded flex items-center justify-center ${
          selected ? '' : 'border border-white/30'
        }`}
        style={{ backgroundColor: selected ? '#C9A84C' : 'transparent' }}
      >
        {selected && <Check className="w-3 h-3 text-black" />}
      </div>
      <span className={selected ? 'text-[#1A1A2E]' : 'text-[#4A4A5A]'}>{label}</span>
    </div>
  </button>
);

export const NavigationButton = ({ onClick, disabled, loading, isComplete, children }) => (
  <Button
    onClick={onClick}
    disabled={disabled || loading}
    data-testid="onboarding-continue-button"
    className="w-full rounded-full py-6 text-lg transition-all duration-300"
    style={{
      background: isComplete 
        ? 'linear-gradient(135deg, #C9A84C 0%, #72826F 100%)'
        : 'rgba(201, 168, 76, 0.8)',
      color: '#FBF9F6'
    }}
  >
    {loading ? (
      <span className="flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Saving...
      </span>
    ) : isComplete ? (
      <span className="flex items-center gap-2">
        <Sparkles className="w-5 h-5" />
        {children}
      </span>
    ) : (
      children
    )}
  </Button>
);
